import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../supabaseAuth';
import SuspensionCheck from '../components/common/SuspensionCheck';
import { GoldIcon, SilverIcon, PlatinumIcon, PaymentIcon, DeliveryIcon } from '../components/icons/MetalIcons';
import SubscriptionWizard from '../components/subscriptions/SubscriptionWizard';
import SubscriptionDetailModal from '../components/subscriptions/SubscriptionDetailModal';
import OnboardingModal from '../components/subscriptions/OnboardingModal';

// Actions
import { fetchSubscriptions, resetSubscriptionCreated } from '../store/slices/subscriptionSlice';
import { fetchMetalPrices } from '../store/slices/metalPriceSlice';
import { fetchTransactions } from '../store/slices/transactionSlice';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAccessToken } = useSupabase();

  // Debug component mount/unmount
  React.useEffect(() => {
    console.log('Dashboard MOUNTED');
    return () => {
      console.log('Dashboard UNMOUNTING');
    };
  }, []);

  const [userProfile, setUserProfile] = React.useState(null);
  const [showWizard, setShowWizard] = React.useState(false);
  const [showDetailModal, setShowDetailModal] = React.useState(false);
  const [selectedSubscription, setSelectedSubscription] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('active');
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  const [showOnboarding, setShowOnboarding] = React.useState(false);
  const [wizardPreset, setWizardPreset] = React.useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = React.useState(false);

  // Debug isAuthenticated changes
  React.useEffect(() => {
    console.log('Dashboard: isAuthenticated changed to:', isAuthenticated);
  }, [isAuthenticated]);

  // Debug showWizard changes
  React.useEffect(() => {
    console.log('Dashboard: showWizard changed to:', showWizard);
  }, [showWizard]);

  const { subscriptions = [], loading: subscriptionsLoading, subscriptionCreated } = useSelector((state) => state.subscriptions);
  const { prices: metalPrices = {}, loading: pricesLoading } = useSelector((state) => state.metalPrices);
  const { transactions = [], loading: transactionsLoading } = useSelector((state) => state.transactions);

  // Fetch user profile from database
  const fetchUserProfile = React.useCallback(async () => {
    try {
      const token = await getAccessToken();
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.getauric.dev';

      const response = await fetch(`${apiBaseUrl}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const profileData = await response.json();
        setUserProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  }, [getAccessToken]);

  React.useEffect(() => {
    const loadInitialData = async () => {
      // Always fetch metal prices (public data)
      dispatch(fetchMetalPrices());

      // Only fetch user-specific data if authenticated
      if (isAuthenticated) {
        await Promise.all([
          dispatch(fetchSubscriptions()),
          dispatch(fetchTransactions({ limit: 10 })),
          fetchUserProfile()
        ]);
      }
      setInitialLoadComplete(true);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh subscriptions and transactions every 60 seconds
  React.useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      console.log('Auto-refreshing subscriptions and transactions...');
      dispatch(fetchSubscriptions());
      dispatch(fetchTransactions({ limit: 10 }));
    }, 60000); // 60 seconds

    return () => clearInterval(intervalId);
  }, [isAuthenticated, dispatch]);

  // Only show loading spinner on initial load, not on background refreshes
  const loading = !initialLoadComplete && (pricesLoading || (isAuthenticated && (subscriptionsLoading || transactionsLoading)));

  // Show onboarding modal for new users with no subscriptions
  React.useEffect(() => {
    if (initialLoadComplete && isAuthenticated && subscriptions.length === 0 && !onboardingDismissed && !showWizard) {
      setShowOnboarding(true);
    }
  }, [initialLoadComplete, isAuthenticated, subscriptions.length, onboardingDismissed, showWizard]);

  const handleOnboardingPreset = (preset) => {
    setShowOnboarding(false);
    setWizardPreset(preset);
    setShowWizard(true);
  };

  const handleOnboardingCustomize = () => {
    setShowOnboarding(false);
    setWizardPreset(null);
    setShowWizard(true);
  };

  const handleOnboardingDismiss = () => {
    setShowOnboarding(false);
    setOnboardingDismissed(true);
  };

  // Sort subscriptions by status priority (active, paused, cancelled) with null check
  const sortedSubscriptions = React.useMemo(() => {
    if (!subscriptions) return [];

    const statusPriority = {
      'active': 1,
      'paused': 2,
      'cancelled': 3
    };

    return [...subscriptions].sort((a, b) => {
      const statusA = statusPriority[a.status?.toLowerCase()] || 4;
      const statusB = statusPriority[b.status?.toLowerCase()] || 4;

      if (statusA !== statusB) {
        return statusA - statusB;
      }

      return a.id - b.id;
    });
  }, [subscriptions]);

  // Filter subscriptions based on status filter
  const filteredSubscriptions = React.useMemo(() => {
    if (statusFilter === 'all') {
      return sortedSubscriptions;
    }
    return sortedSubscriptions.filter(sub => sub.status?.toLowerCase() === statusFilter);
  }, [sortedSubscriptions, statusFilter]);

  // Get active subscriptions for display
  const activeSubscriptions = filteredSubscriptions;

  // Get recent transactions with null check
  const recentTransactions = transactions?.slice?.(0, 5) || [];

  // Handlers for modals
  const handleOpenWizard = React.useCallback(() => {
    setShowOnboarding(true);
  }, []);

  const handleCloseWizard = React.useCallback(() => {
    setShowWizard(false);
    setShowOnboarding(false);
    setWizardPreset(null); // Reset preset so next open starts fresh
    setOnboardingDismissed(true); // Don't auto-show onboarding again this session
    // Refresh subscriptions list when wizard closes (in case a new subscription was created)
    dispatch(fetchSubscriptions());
  }, [dispatch]);

  const handleSubscriptionClick = React.useCallback((subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailModal(true);
  }, []);

  const handleCloseDetailModal = React.useCallback(() => {
    setShowDetailModal(false);
  }, []);

  // Handle subscription created
  React.useEffect(() => {
    console.log('Dashboard: subscriptionCreated changed to:', subscriptionCreated);
    if (subscriptionCreated) {
      console.log('Dashboard: Fetching subscriptions due to subscriptionCreated=true');
      dispatch(fetchSubscriptions());

      const timer = setTimeout(() => {
        dispatch(resetSubscriptionCreated());
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [subscriptionCreated, dispatch]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to get metal color (for subscription items)
  const getMetalColor = (metal) => {
    switch(metal.toLowerCase()) {
      case 'gold': return 'bg-yellow-100';
      case 'silver': return 'bg-gray-200';
      case 'platinum': return 'bg-gray-300';
      case 'copper': return 'bg-orange-200';
      default: return 'bg-gray-200';
    }
  };

  // Helper function to get metal icon component
  const getMetalIcon = (metal) => {
    switch(metal.toLowerCase()) {
      case 'gold': return <GoldIcon />;
      case 'silver': return <SilverIcon />;
      case 'platinum': return <PlatinumIcon />;
      default: return <GoldIcon />;
    }
  };

  // Helper function to get price change indicator
  const getPriceChange = (current, previous) => {
    if (!previous || !current || isNaN(current) || isNaN(previous)) return null;

    const change = ((current - previous) / previous) * 100;
    const isPositive = change >= 0;

    return (
      <span className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {Math.abs(change).toFixed(2)}% {isPositive ? '↑' : '↓'}
      </span>
    );
  };

  // Helper function to get price and unit for display
  const getPriceDisplay = (metal, data) => {
    if (!data) return { price: 'N/A', unit: 'oz' };

    // For all metals, use price_per_ounce
    return { price: formatCurrency(data.price_per_ounce), unit: 'oz' };
  };

  // Define metal order
  const metalOrder = ['gold', 'silver', 'platinum'];

  // Sort metals according to the defined order
  const sortedMetalPrices = metalOrder
    .filter(metal => metalPrices[metal])
    .map(metal => [metal, metalPrices[metal]]);

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      let date;

      if (dateString instanceof Date) {
        date = dateString;
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        const timestamp = Date.parse(dateString);
        if (!isNaN(timestamp)) {
          date = new Date(timestamp);
        } else {
          return 'N/A';
        }
      }

      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', dateString);
      return 'N/A';
    }
  };

  // Format date to show full UTC date and time (hour:minute)
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    // Format: YYYY-MM-DD HH:MM UTC
    return date.toISOString().replace('T', ' ').substring(0, 16) + ' UTC';
  };

  // Format metal weight based on metal type and weight type
  const formatMetalWeight = (weight, metalType, weightType) => {
    if (!weight) return 'N/A';

    // If we have a weight_type from the transaction, use it directly
    if (weightType) {
      const formattedWeight = weight % 1 === 0 ? weight.toString() : weight.toFixed(4).replace(/\.?0+$/, '');
      return `${formattedWeight} ${weightType}`;
    }

    // Legacy: Convert from grams to appropriate unit
    switch (metalType?.toLowerCase()) {
      case 'gold':
      case 'silver':
      case 'platinum':
        const oz = weight / 31.1035;
        return `${oz.toFixed(4)} oz`;
      default:
        return `${weight.toFixed(4)} g`;
    }
  };

  // Get transaction icon background color
  const getTransactionIconBg = (type) => {
    switch(type) {
      case 'payment': return 'bg-green-500';
      case 'delivery': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get status badge styles
  const getStatusBadgeClass = (status, type) => {
    if (type === 'delivery' && status === 'completed') {
      return 'bg-blue-100 text-blue-600';
    }
    switch(status) {
      case 'completed':
      case 'paid':
        return 'bg-green-100 text-green-600';
      case 'pending':
        return 'bg-yellow-100 text-yellow-600';
      case 'shipped':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-red-100 text-red-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen bg-gray-50 px-5 md:px-12 pt-24 pb-2 flex flex-col">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-4">
        <div>
          {isAuthenticated ? (
            <>
              {userProfile && (
                <h1 className="text-3xl font-semibold text-black">
                  {(() => {
                    const firstName = userProfile?.first_name?.trim();
                    const displayName = (firstName && firstName.length > 0) ? firstName : (user?.email || 'Customer');
                    return `Welcome to Auric, ${displayName}!`;
                  })()}
                </h1>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-semibold text-black">Auric - Precious Metals Investment</h1>
              <p className="text-gray-600 text-lg mt-1">Track precious metal prices and start building your portfolio</p>
            </>
          )}
        </div>
        {isAuthenticated && (
          <button
            onClick={handleOpenWizard}
            className="bg-black text-white px-6 py-2 rounded-full font-bold hover:bg-gray-800 transition-colors whitespace-nowrap"
          >
            New Goal
          </button>
        )}
        {!isAuthenticated && (
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/login')}
              className="bg-black text-white px-6 py-2 rounded-full font-bold hover:bg-gray-800 transition-colors"
            >
              Login
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="bg-white text-black px-6 py-2 rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
            >
              Sign Up
            </button>
          </div>
        )}
      </div>

      {/* Bottom section wrapper */}
      <div className="flex flex-col mt-2">
        {/* Onboarding Modal for new users */}
        <OnboardingModal
          show={showOnboarding}
          onHide={handleOnboardingDismiss}
          onSelectPreset={handleOnboardingPreset}
          onCustomize={handleOnboardingCustomize}
        />

        {/* Subscription Wizard Modal */}
        <SubscriptionWizard
          key="subscription-wizard"
          show={isAuthenticated && showWizard}
          onHide={handleCloseWizard}
          initialData={wizardPreset}
        />

      {/* Subscription Detail Modal */}
      {isAuthenticated && showDetailModal && (
        <SubscriptionDetailModal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          subscription={selectedSubscription}
          metalPrices={metalPrices}
        />
      )}

      {/* Authenticated User Sections */}
      {isAuthenticated && (
        <SuspensionCheck>
          {/* Two Column Layout */}
          <div className="flex flex-col lg:flex-row gap-4 w-full h-[calc(100vh-210px)]">
            {/* Left Column - Subscriptions */}
            <div className="flex-1 flex flex-col gap-1 min-h-0">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-black">My Goals</h2>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-sm px-3 py-1 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent mr-6"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div className="bg-white rounded-xl p-3 shadow-sm flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">
                {activeSubscriptions.length > 0 ? (
                  activeSubscriptions.map((subscription) => (
                    <div
                      key={subscription.id}
                      className="flex items-center justify-between p-3 bg-gray-100 rounded-lg h-[76px] cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => handleSubscriptionClick(subscription)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                          {getMetalIcon(subscription.metal_type)}
                        </div>
                        <div>
                          <h4 className="text-base font-medium text-black capitalize">
                            {subscription.metal_type || 'Metal'} Subscription
                          </h4>
                          <p className="text-xs font-normal text-gray-800">
                            Next: {formatDate(subscription.next_billing_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-medium text-gray-700">
                          {formatCurrency(subscription.amount_per_period)}/{subscription.period || 'month'}
                        </span>
                        <span className="text-xs text-gray-600">
                          Click to view details
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="mb-3 text-gray-600">
                      {statusFilter === 'all'
                        ? "Create your first subscription to begin accumulating gold, silver, or platinum."
                        : `You don't have any ${statusFilter} subscriptions.`}
                    </p>
                    {statusFilter === 'active' && (
                      <button
                        onClick={handleOpenWizard}
                        className="bg-black text-white px-6 py-2 rounded-full font-bold hover:bg-gray-800 transition-colors"
                      >
                        Start a new goal
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Recent Activity */}
            <div className="flex-1 flex flex-col gap-1 min-h-0">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-bold text-black">Recent Activity</h2>
                <button
                  onClick={() => navigate('/transactions')}
                  className="text-sm font-bold text-black hover:underline"
                >
                  View All →
                </button>
              </div>
              <div className="bg-white rounded-xl p-3 shadow-sm flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">
                {recentTransactions.length > 0 ? (
                  recentTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-3 bg-gray-100 rounded-lg h-[76px]"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${getTransactionIconBg(transaction.transaction_type)} flex items-center justify-center text-white`}>
                          {transaction.transaction_type === 'payment' ? <PaymentIcon /> : <DeliveryIcon />}
                        </div>
                        <div>
                          <h4 className="text-base font-medium text-black">
                            {transaction.transaction_type === 'payment' ? 'Purchase' : 'Delivery'}{transaction.metal_type ? ` - ${transaction.metal_type.charAt(0).toUpperCase() + transaction.metal_type.slice(1)}` : ''}
                          </h4>
                          <p className="text-xs font-normal text-gray-800">
                            {formatDateTime(transaction.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="font-medium text-gray-700">
                          {transaction.amount ? formatCurrency(transaction.amount) : 'N/A'}
                        </span>
                        <span className={`px-5 py-1 rounded-xl text-xs font-medium ${getStatusBadgeClass(transaction.status, transaction.transaction_type)}`}>
                          {transaction.transaction_type === 'payment' && transaction.status === 'completed'
                            ? 'Paid'
                            : transaction.transaction_type === 'delivery' && transaction.status === 'completed'
                            ? 'Shipped'
                            : transaction.status}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-600">
                    <p>No recent transactions.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SuspensionCheck>
      )}

      {/* Public Call-to-Action for non-authenticated users */}
      {!isAuthenticated && (
        <div className="w-full">
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <h4 className="text-2xl font-semibold text-black mb-3">Start Your Precious Metals Journey</h4>
            <p className="text-gray-600 mb-6">
              Create an account to start investing in gold, silver, and platinum with our subscription-based service.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => navigate('/signup')}
                className="bg-black text-white px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-800 transition-colors"
              >
                Get Started
              </button>
              <button
                onClick={() => navigate('/login')}
                className="bg-white text-black px-8 py-3 rounded-full font-bold text-lg border-2 border-black hover:bg-gray-50 transition-colors"
              >
                Already have an account?
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Footer */}
        <footer className="w-full pt-2">
          <p className="text-sm text-gray-600 text-right pb-3">Copyright 2025 Auric</p>
        </footer>
      </div>
    </div>
  );
};

export default Dashboard;
